import { KeyboardEvent } from 'react';

import formatPrice from 'utils/formatPrice';
import { IProduct } from 'models';

import { useCart } from 'contexts/cart-context';
import { useI18n } from 'contexts/i18n-context';

import * as S from './style';

interface IProps {
  product: IProduct;
  onToggleFavourite?(product: IProduct): void;
}

const Product = ({ onToggleFavourite, product }: IProps) => {
  const { addProduct } = useCart();
  const { t } = useI18n();
  const {
    title,
    description,
    imageUrl,
    price,
    packageSpec,
    unit,
    installments,
    currencyId,
    currencyFormat,
    isFreeShipping,
  } = product;

  const formattedPrice = price === null ? null : formatPrice(price, currencyId);
  let productInstallment;

  if (price !== null && installments) {
    const installmentPrice = price / installments;

    productInstallment = (
      <S.Installment>
        <span>{t('product.installment', { installments })}</span>
        <b>
          {currencyFormat}
          {formatPrice(installmentPrice, currencyId)}
        </b>
      </S.Installment>
    );
  }

  const handleAddProduct = () => {
    addProduct({ ...product, quantity: 1 });
  };

  const handleAddProductWhenEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.code === 'Space') {
      addProduct({ ...product, quantity: 1 });
    }
  };

  return (
    <S.Container onKeyUp={handleAddProductWhenEnter} tabIndex={1}>
      {onToggleFavourite && (
        <S.FavouriteButton
          type="button"
          onClick={() => onToggleFavourite(product)}
          aria-pressed={Boolean(product.isFavourite)}
          aria-label={
            product.isFavourite
              ? 'Remove from favourites'
              : 'Add to favourites'
          }
        >
          {product.isFavourite ? '★' : '☆'}
        </S.FavouriteButton>
      )}
      {isFreeShipping && <S.Stopper>{t('product.freeShipping')}</S.Stopper>}
      {imageUrl?.trim() && (
        <S.ImageFrame aria-label={t('product.image')}>
          <S.Image src={imageUrl} alt={title} />
        </S.ImageFrame>
      )}
      <S.Category>{description}</S.Category>
      <S.Title>{title}</S.Title>
      {(packageSpec || unit) && (
        <S.Description>
          {[packageSpec, unit].filter(Boolean).join(' / ')}
        </S.Description>
      )}
      <S.Price>
        {formattedPrice ? (
          <S.Val>
            <small>{currencyFormat}</small>
            <b>{formattedPrice.substring(0, formattedPrice.length - 3)}</b>
            <span>{formattedPrice.substring(formattedPrice.length - 3)}</span>
          </S.Val>
        ) : (
          <S.Unpriced>{t('product.priceUnavailable')}</S.Unpriced>
        )}
        {productInstallment}
      </S.Price>
      <S.BuyButton onClick={handleAddProduct} tabIndex={-1}>
        {t('product.addToCart')}
      </S.BuyButton>
    </S.Container>
  );
};

export default Product;
